import React, { useState } from 'react';
import './JsonCodeViewer.css';

function getType(val) {
  if (Array.isArray(val)) return 'array';
  if (val === null) return 'null';
  return typeof val;
}

function formatJsonLines(obj, collapsedPaths, path = '', depth = 0, addComma = false) {
  const lines = [];
  const type = getType(obj);
  const isCollapsed = collapsedPaths[path];

  if (type === 'object' && obj !== null) {
    const keys = Object.keys(obj);
    // Opening line with toggle
    lines.push({
      text: '{',
      depth,
      path,
      collapsible: keys.length > 0,
      collapsed: isCollapsed,
      type: 'object-open',
      addComma: addComma && keys.length === 0,
    });
    if (isCollapsed && keys.length > 0) {
      lines.push({
        text: '{...}' + (addComma ? ',' : ''),
        depth: depth + 1,
        path: path + '.collapsed',
        collapsible: false,
        collapsed: false,
        type: 'collapsed',
        addComma: false,
      });
    } else {
      keys.forEach((key, idx) => {
        const val = obj[key];
        const valType = getType(val);
        const childPath = path ? `${path}.${key}` : key;
        const isLast = idx === keys.length - 1;
        if ((valType === 'object' || valType === 'array') && collapsedPaths[childPath]) {
          // If collapsed, show only {...} or [...] for this property
          lines.push({
            text: `"${key}": ${valType === 'object' ? "{...}" : "[...]"}${!isLast ? ',' : ''}`,
            depth: depth + 1,
            path: childPath,
            collapsible: false,
            collapsed: false,
            type: 'collapsed-property',
            addComma: false,
          });
        } else if (valType === 'object' || valType === 'array') {
          // Property line
          lines.push({
            text: `"${key}":`,
            depth: depth + 1,
            path: childPath,
            collapsible: false,
            collapsed: false,
            type: 'property',
            addComma: false,
          });
          // Nested structure with toggle only on opening line
          lines.push(...formatJsonLines(val, collapsedPaths, childPath, depth + 2, !isLast));
        } else {
          lines.push({
            text: `"${key}": ${JSON.stringify(val)}${!isLast ? ',' : ''}`,
            depth: depth + 1,
            path: childPath,
            collapsible: false,
            collapsed: false,
            type: 'value',
            addComma: false,
          });
        }
      });
    }
    // Closing line
    lines.push({
      text: '}' + (addComma ? ',' : ''),
      depth,
      path: path + '.close',
      collapsible: false,
      collapsed: false,
      type: 'object-close',
      addComma: false,
    });
  } else if (type === 'array') {
    // Opening line with toggle
    lines.push({
      text: '[',
      depth,
      path,
      collapsible: obj.length > 0,
      collapsed: isCollapsed,
      type: 'array-open',
      addComma: addComma && obj.length === 0,
    });
    if (isCollapsed && obj.length > 0) {
      lines.push({
        text: '[...]' + (addComma ? ',' : ''),
        depth: depth + 1,
        path: path + '.collapsed',
        collapsible: false,
        collapsed: false,
        type: 'collapsed',
        addComma: false,
      });
    } else {
      obj.forEach((val, idx) => {
        const valType = getType(val);
        const childPath = path ? `${path}[${idx}]` : `[${idx}]`;
        const isLast = idx === obj.length - 1;
        if ((valType === 'object' || valType === 'array') && collapsedPaths[childPath]) {
          lines.push({
            text: valType === 'object' ? '{...}' : '[...]' + (!isLast ? ',' : ''),
            depth: depth + 1,
            path: childPath,
            collapsible: false,
            collapsed: false,
            type: 'collapsed',
            addComma: false,
          });
        } else if (valType === 'object' || valType === 'array') {
          lines.push(...formatJsonLines(val, collapsedPaths, childPath, depth + 1, !isLast));
        } else {
          lines.push({
            text: `${JSON.stringify(val)}${!isLast ? ',' : ''}`,
            depth: depth + 1,
            path: childPath,
            collapsible: false,
            collapsed: false,
            type: 'value',
            addComma: false,
          });
        }
      });
    }
    // Closing line
    lines.push({
      text: ']' + (addComma ? ',' : ''),
      depth,
      path: path + '.close',
      collapsible: false,
      collapsed: false,
      type: 'array-close',
      addComma: false,
    });
  }
  return lines;
}

export default function JsonCodeViewer({ json, style }) {
  const [collapsedPaths, setCollapsedPaths] = useState({});
  const [copied, setCopied] = useState(false);

  if (!json) return null;

  const lines = formatJsonLines(json, collapsedPaths);

  const handleToggle = (path) => {
    setCollapsedPaths(prev => ({ ...prev, [path]: !prev[path] }));
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(json, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="json-code-viewer" style={style}>
      {/* Copy icon button */}
      <button
        className="json-copy-btn"
        onClick={handleCopy}
        title="Copy JSON"
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          zIndex: 10,
          padding: 0,
          margin: 0
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#388e3c" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="8" y="8" width="12" height="12" rx="3"/>
          <rect x="4" y="4" width="12" height="12" rx="3"/>
        </svg>
        {copied && (
          <span style={{
            position: 'absolute',
            top: 28,
            right: 0,
            background: '#388e3c',
            color: '#fff',
            borderRadius: 4,
            padding: '2px 8px',
            fontSize: 12,
            zIndex: 20
          }}>Copied!</span>
        )}
      </button>
      <div className="json-lines">
        {lines.map((line, idx) => (
          <div className="json-line" key={idx}>
            <span className="json-gutter">
              <span className="json-line-number">{idx + 1}</span>
              {((line.type === 'object-open' || line.type === 'array-open') && line.collapsible) && (
                <span
                  className="json-toggle"
                  onClick={() => handleToggle(line.path)}
                  title={line.collapsed ? 'Expand' : 'Collapse'}
                >
                  {line.collapsed ? '\u25B6' : '\u25BC'}
                </span>
              )}
              {(line.type === 'collapsed-property' || line.type === 'collapsed') && (
                <span
                  className="json-toggle"
                  onClick={() => handleToggle(line.path)}
                  title={'Expand'}
                >
                  {'\u25B6'}
                </span>
              )}
            </span>
            <span
              className="json-text"
              style={{ paddingLeft: `${line.depth * 16}px` }}
            >
              {line.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
} 